/**
 * AvailabilitySummary component
 * Displays who is available for a specific date/meal slot
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AvailabilitySummary as AvailabilitySummaryType } from '@/types/api';
import { useQuery } from '@tanstack/react-query';
import { getUsers } from '@/lib/api/users';

export interface AvailabilitySummaryProps {
  /** Availability summary data for this slot */
  summary?: AvailabilitySummaryType;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Compact mode shows only count */
  compact?: boolean;
  /** Show user names inline (for detailed mode) */
  detailed?: boolean;
  /** Show user names inline in compact mode */
  showUserNames?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Shows availability summary with expandable user list
 *
 * Features:
 * - Displays count of available users
 * - Expandable list of user names
 * - Compact mode for grid display
 * - Detailed mode with organizer badges
 * - Inline user names option
 * - Loading state support
 *
 * @example
 * ```tsx
 * <AvailabilitySummary
 *   summary={availabilityData}
 *   isLoading={false}
 *   compact={false}
 *   detailed={true}
 *   showUserNames={true}
 * />
 * ```
 */
export function AvailabilitySummary({
  summary,
  isLoading = false,
  compact = false,
  detailed = false,
  showUserNames = false,
  className = '',
}: AvailabilitySummaryProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch all users to display names
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  if (isLoading) {
    return (
      <div className={`text-xs text-gray-500 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-16" />
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className={`text-xs text-gray-500 ${className}`}>
        {t('menu.noAvailabilityData')}
      </div>
    );
  }

  const presentCount = summary.presentUsers.length;
  const hasUsers = presentCount > 0;

  // Get user details for present users (with organizer flag)
  const presentUsersDetails =
    usersData?.users
      .filter((user) => summary.presentUsers.some(pu => pu.userId === user.id))
      .map((user) => ({
        id: user.id,
        name: user.name,
        isOrganizer: user.isOrganizer,
      })) || [];

  const presentUserNames = presentUsersDetails.map((user) => user.name);

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`text-xs font-medium ${
          hasUsers ? 'text-green-700' : 'text-gray-500'
        } hover:underline ${className}`}
        title={hasUsers ? t('menu.show') + ' ' + t('menu.names') : t('menu.noOneAvailable')}
      >
        {presentCount}
        {hasUsers && (isExpanded || showUserNames) && presentUserNames.length > 0 && (
          <span className="ml-1 text-xs text-gray-600">
            ({presentUserNames.join(', ')})
          </span>
        )}
      </button>
    );
  }

  // Detailed mode with enhanced user information
  if (detailed) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {t('menu.availability')}: {presentCount}
          </span>
          {hasUsers && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              aria-expanded={isExpanded}
            >
              {isExpanded ? t('menu.hide') : t('menu.show')} {t('menu.details')}
            </button>
          )}
        </div>

        {isExpanded && hasUsers && presentUsersDetails.length > 0 && (
          <div className="pl-3 border-l-2 border-green-200 space-y-2">
            {presentUsersDetails.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between bg-green-50 rounded-md px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-green-600 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    {user.name}
                  </span>
                </div>
                {user.isOrganizer && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                    title={t('header.organizer')}
                  >
                    {t('header.organizer')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {!hasUsers && (
          <p className="text-xs text-gray-500 italic">{t('menu.noOneAvailable')}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          {t('menu.availability')}: {presentCount}
        </span>
        {hasUsers && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            {isExpanded ? t('menu.hide') : t('menu.show')} {t('menu.names')}
          </button>
        )}
      </div>

      {isExpanded && hasUsers && presentUsersDetails.length > 0 && (
        <div className="pl-3 border-l-2 border-green-200">
          <ul className="space-y-1">
            {presentUsersDetails.map((user) => (
              <li
                key={user.id}
                className="text-sm text-gray-600 flex items-center justify-between"
              >
                <div className="flex items-center">
                  <svg
                    className="w-3 h-3 text-green-500 mr-2 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{user.name}</span>
                </div>
                {user.isOrganizer && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 ml-2"
                    title={t('header.organizer')}
                  >
                    Org
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!hasUsers && (
        <p className="text-xs text-gray-500 italic">{t('menu.noOneAvailable')}</p>
      )}
    </div>
  );
}
